import React from "react";
import AddIcon from "@mui/icons-material/Add";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";

import EmployeeProfileTabHeader from "./EmployeeProfileTabHeader";
import EmployeeProfileSectionCard from "./EmployeeProfileSectionCard";
import EmployeeProfileEmptyState from "./EmployeeProfileEmptyState";
import CheckInsSparkline from "./CheckInsSparkline";
import CheckInsOpenActionItemsStrip from "./CheckInsOpenActionItemsStrip";
import CheckInsHistoryCard from "./CheckInsHistoryCard";
// Legacy notes panel intentionally hidden for now; re-enable when ready.
// import CheckInsLegacyNotesPanel from "./CheckInsLegacyNotesPanel";
import CheckInForm from "./CheckInForm/CheckInForm";

import LoadSpinner from "../../../../../utilities/LoadSpinner";
import { getRightworkAPI, postRightworkAPI } from "../../utilities/requests";
import {
  API_get_check_ins_for_employee,
  API_toggle_check_in_action_item,
} from "../../../../../constants";


function EmployeeProfileCheckInsTab(props) {
  const [isLoading, setIsLoading] = React.useState(true);
  const [checkIns, setCheckIns] = React.useState([]);
  const [openActionItems, setOpenActionItems] = React.useState([]);
  const [fetchError, setFetchError] = React.useState(null);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerMode, setDrawerMode] = React.useState("create");
  const [drawerCheckInUuid, setDrawerCheckInUuid] = React.useState(null);

  const stmId = props.stm_id;

  const openCreateDrawer = () => {
    setDrawerMode("create");
    setDrawerCheckInUuid(null);
    setDrawerOpen(true);
  };

  const openViewDrawer = (uuid) => {
    setDrawerMode("view");
    setDrawerCheckInUuid(uuid);
    setDrawerOpen(true);
  };

  const closeDrawer = () => setDrawerOpen(false);

  const handleStripToggle = async (actionItemUuid) => {
    try {
      const res = await postRightworkAPI(API_toggle_check_in_action_item, {
        store_team_member_id: stmId,
        check_in_action_item_uuid: actionItemUuid,
      });
      if (res.status === 200) {
        const updated = res.data?.redux?.payload?.action_item;
        // The strip shows only open items. If the toggle marked it complete,
        // remove it; otherwise reflect the new state in place.
        if (updated?.completed_at) {
          setOpenActionItems((prev) =>
            prev.filter((it) => it.uuid !== actionItemUuid),
          );
        } else if (updated) {
          setOpenActionItems((prev) =>
            prev.map((it) =>
              it.uuid === actionItemUuid
                ? { ...it, completed_at: updated.completed_at }
                : it,
            ),
          );
        }
      }
    } catch (e) {
      // Swallow — user can retry. Could surface a toast in V1.1.
      console.warn("Failed to toggle action item from strip", e);
    }
  };

  // Track the most recent in-flight request so a stale response from a
  // previous employee can't clobber the current employee's data when the
  // user switches profiles quickly.
  const latestRequestIdRef = React.useRef(0);

  const fetchHistory = React.useCallback(async (showLoading = true) => {
    if (!stmId) return;
    const requestId = ++latestRequestIdRef.current;
    // Background refreshes (e.g. after the drawer closes) skip the spinner
    // so the visible list stays in place while the new data lands.
    if (showLoading) setIsLoading(true);
    setFetchError(null);
    try {
      const res = await getRightworkAPI(API_get_check_ins_for_employee, {
        store_team_member_id: stmId,
      });
      if (requestId !== latestRequestIdRef.current) return;  // stale
      if (res.status === 200) {
        const payload = res.data?.redux?.payload;
        setCheckIns(payload?.check_ins || []);
        setOpenActionItems(payload?.open_action_items || []);
      }
    } catch (error) {
      if (requestId !== latestRequestIdRef.current) return;
      setFetchError("Couldn't load check-ins. Please try again.");
    } finally {
      if (requestId === latestRequestIdRef.current) {
        // No-op if we never set it; otherwise clears initial / employee-switch
        // load spinners.
        setIsLoading(false);
      }
    }
  }, [stmId]);

  // Used as CheckInForm's onSaved — refresh quietly so closing the drawer
  // doesn't flash a full-page spinner over the existing list.
  const refreshHistoryQuietly = React.useCallback(() => {
    fetchHistory(false);
  }, [fetchHistory]);

  React.useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Legacy notes panel intentionally hidden for now.
  // const legacyEntries =
  //   props.selectedTeamMemberData?.logbook_employee_entries || [];

  // Build sparkline data: pick the scale field most recently used, so that
  // if a template was edited the user sees the current field's trend (not a
  // legacy one). The backend returns check-ins newest-first; we iterate in
  // that order for discovery and reverse the chosen series at the end so the
  // x-axis runs chronologically.
  const sparkline = React.useMemo(() => {
    const byTemplateUuid = new Map();
    checkIns.forEach((ci) => {
      (ci.scale_responses || []).forEach((sr) => {
        const key = sr.template_field_uuid;
        if (!key) return;
        if (!byTemplateUuid.has(key)) {
          byTemplateUuid.set(key, {
            label: sr.field_label,
            low_threshold: sr.low_threshold,
            points: [],
          });
        }
        byTemplateUuid.get(key).points.push({
          conducted_at: ci.conducted_at,
          value: sr.value,
        });
      });
    });
    if (byTemplateUuid.size === 0) return null;
    // V1: render only the first scale field encountered — which, with
    // newest-first iteration, is the most recently used scale field.
    const first = byTemplateUuid.values().next().value;
    first.points.reverse();
    return first.points.length >= 2 ? first : null;
  }, [checkIns]);

  const hasHistory = checkIns.length > 0;
  // Legacy notes panel intentionally hidden for now.
  // const hasLegacy = legacyEntries.length > 0;
  const hasAnything = hasHistory;

  return (
    <div className="max-w-5xl mx-auto">
      <EmployeeProfileTabHeader
        icon={AssignmentIndIcon}
        title="Check-Ins"
        subtitle="Structured 1-on-1 conversations with this employee."
        actionLabel="New Check-In"
        actionIcon={AddIcon}
        onAction={openCreateDrawer}
      />

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <LoadSpinner />
        </div>
      )}

      {/* Fetch error */}
      {!isLoading && fetchError && (
        <EmployeeProfileSectionCard bodyClassName="">
          <div className="p-6 text-sm text-rose-600">{fetchError}</div>
        </EmployeeProfileSectionCard>
      )}

      {/* Main content */}
      {!isLoading && !fetchError && (
        <>
          {/* Sparkline */}
          {sparkline && (
            <div className="mb-4">
              <CheckInsSparkline
                label={sparkline.label}
                lowThreshold={sparkline.low_threshold}
                points={sparkline.points}
              />
            </div>
          )}

          {/* Open action items strip */}
          {openActionItems.length > 0 && (
            <div className="mb-4">
              <CheckInsOpenActionItemsStrip
                items={openActionItems}
                onToggle={handleStripToggle}
              />
            </div>
          )}

          {/* History */}
          {hasHistory ? (
            <EmployeeProfileSectionCard
              header="Past check-ins"
              bodyClassName="divide-y divide-gray-100 overflow-hidden"
            >
              {checkIns.map((ci) => (
                <CheckInsHistoryCard
                  key={ci.uuid}
                  checkIn={ci}
                  onViewFull={() => openViewDrawer(ci.uuid)}
                />
              ))}
            </EmployeeProfileSectionCard>
          ) : (
            !hasAnything && (
              <EmployeeProfileSectionCard bodyClassName="">
                <EmployeeProfileEmptyState
                  icon={AssignmentIndIcon}
                  title="No check-ins yet"
                  description="No check-ins recorded yet. Use 'New Check-In' above to start a structured 1-on-1 — your history will build here over time."
                />
              </EmployeeProfileSectionCard>
            )
          )}

          {/* Legacy notes panel intentionally hidden for now. Re-enable by
              restoring the import + `legacyEntries`/`hasLegacy` and uncommenting
              this block. */}

          {/*
          {hasLegacy && (
            <div className="mt-6">
              <CheckInsLegacyNotesPanel
                entries={legacyEntries}
                get_file_upload={props.get_file_upload}
              />
            </div>
          )}
          */}
        </>
      )}

      {/* Drawer for create/view */}
      <CheckInForm
        open={drawerOpen}
        onClose={closeDrawer}
        mode={drawerMode}
        checkInUuid={drawerCheckInUuid}
        employeeStmId={stmId}
        onSaved={refreshHistoryQuietly}
      />
    </div>
  );
}

export default EmployeeProfileCheckInsTab;
